import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styles from "../styles/reviews.module.css";

/**
 * InfiniteCarousel.jsx
 * - Desktop: дублирует элементы 3 раза и крутит бесконечно
 * - Mobile (<=767): анимации нет
 * - Direction: left/right
 * - Hover: pause
 *
 * ВАЖНО: НЕ используем setState на каждом кадре (иначе max update depth).
 */
const InfiniteCarousel = ({ children, speed = 0.5, direction = "left" }) => {
  const trackRef = useRef(null);

  // mobile state (чтобы реагировать на resize)
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });

  // hover pause
  const hoveredRef = useRef(false);

  // animation refs
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);
  const offsetRef = useRef(0);
  const oneSetWidthRef = useRef(0);

  // children -> array
  const originalItems = useMemo(() => React.Children.toArray(children), [children]);

  // duplicate items
  const duplicatedItems = useMemo(() => {
    if (isMobile) return originalItems;

    // 3x duplication
    return Array(3)
      .fill(null)
      .flatMap((_, i) =>
        originalItems.map((child, j) =>
          React.isValidElement(child)
            ? React.cloneElement(child, { key: `dup-${i}-${j}` })
            : <React.Fragment key={`dup-${i}-${j}`}>{child}</React.Fragment>
        )
      );
  }, [originalItems, isMobile]);

  // mobile listener
  useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia("(max-width: 767px)");
    const handler = () => setIsMobile(mq.matches);

    handler();

    // safari fallback
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  // measure width (1/3 track)
  const measure = () => {
    const el = trackRef.current;
    if (!el) return;

    const totalWidth = el.scrollWidth || 0;
    const oneSet = isMobile ? totalWidth : totalWidth / 3;

    oneSetWidthRef.current = oneSet;

    // стартовая позиция
    if (direction === "right") {
      offsetRef.current = -oneSet; // справа начинаем “с конца”
    } else {
      offsetRef.current = -oneSet; // держим центр в середине, чтобы бесшовно
    }

    // применяем сразу
    el.style.transform = `translate3d(${offsetRef.current}px, 0, 0)`;
  };

  // initial measure + on deps
  useLayoutEffect(() => {
    measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicatedItems.length, isMobile, direction]);

  // measure on resize
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicatedItems.length, isMobile, direction]);

  const tick = (ts) => {
    const el = trackRef.current;
    if (!el) return;

    const oneSet = oneSetWidthRef.current || 0;
    if (!oneSet) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }

    const last = lastTsRef.current || ts;
    const dt = (ts - last) / 1000;
    lastTsRef.current = ts;

    if (!hoveredRef.current && !isMobile) {
      // speed у тебя был “px за frame”, но лучше “px/sec”.
      // Чтобы сохранить поведение почти как раньше: умножаем на 60.
      const pxPerSec = speed * 60;

      const dir = direction === "left" ? -1 : 1;
      offsetRef.current += dir * pxPerSec * dt;

      // бесшовность: держим offset в диапазоне (-2*oneSet .. 0)
      if (direction === "left" && Math.abs(offsetRef.current) >= oneSet * 2) {
        offsetRef.current = -oneSet;
      }
      if (direction === "right" && offsetRef.current >= 0) {
        offsetRef.current = -oneSet;
      }

      el.style.transform = `translate3d(${offsetRef.current}px, 0, 0)`;
    }

    rafRef.current = requestAnimationFrame(tick);
  };

  // start/stop animation
  useEffect(() => {
    // на мобилках выключено
    if (isMobile) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTsRef.current = 0;
      return;
    }

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTsRef.current = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, direction, speed]);

  return (
    <div
      className={styles.carousel}
      onMouseEnter={() => (hoveredRef.current = true)}
      onMouseLeave={() => (hoveredRef.current = false)}
    >
      <div
        ref={trackRef}
        className={styles.track}
        style={{
          // на мобилках не двигаем transform (оставим как есть)
          willChange: isMobile ? "auto" : "transform",
          transform: isMobile ? undefined : "translate3d(0,0,0)",
        }}
      >
        {duplicatedItems}
      </div>
    </div>
  );
};

export default InfiniteCarousel;
